import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Upload, X, Check, Loader2, Image, ChevronRight, Info } from "lucide-react";
import { format, addDays } from "date-fns";
import BackButton from "@/components/BackButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 24 hourly time slots (01:00 - 24:00)
// Display shows 01:00-24:00, but hour 24 is stored as hour 0 (next day concept)
const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const displayHour = i + 1; // 1-24
  const storageHour = displayHour === 24 ? 0 : displayHour; // 24:00 maps to hour 0
  return {
    hour: storageHour,
    displayHour: displayHour,
    label: `${String(displayHour).padStart(2, '0')}:00`,
    range: `${String(displayHour).padStart(2, '0')}:00 - ${String(displayHour).padStart(2, '0')}:59`,
  };
});

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const infoFileInputRef = useRef(null);
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [infoFiles, setInfoFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [infoDragActive, setInfoDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [infoUploadStatus, setInfoUploadStatus] = useState({});
  const [activeTab, setActiveTab] = useState("charts");

  // Assign files to timeslots in selection order
  const assignFilesToSlots = (selectedFiles) => {
    const imageFiles = Array.from(selectedFiles).filter(f => f.type.startsWith("image/"));
    const newFiles = imageFiles.slice(0, 24).map((file, index) => {
      const hour = TIME_SLOTS[index]?.hour;
      return {
        file,
        hour,
        preview: URL.createObjectURL(file),
      };
    });
    setFiles(newFiles);
    setUploadStatus({});
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) {
      assignFilesToSlots(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files) {
      assignFilesToSlots(e.target.files);
    }
  };

  const clearFiles = () => {
    files.forEach(f => URL.revokeObjectURL(f.preview));
    setFiles([]);
    setUploadStatus({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Info file helpers
  const assignInfoFilesToSlots = (selectedFiles) => {
    const imageFiles = Array.from(selectedFiles).filter(f => f.type.startsWith("image/"));
    const newFiles = imageFiles.slice(0, 24).map((file, index) => {
      const hour = TIME_SLOTS[index]?.hour;
      const displayHour = TIME_SLOTS[index]?.displayHour;
      return { file, hour, displayHour, preview: URL.createObjectURL(file) };
    });
    setInfoFiles(newFiles);
    setInfoUploadStatus({});
  };

  const clearInfoFiles = () => {
    infoFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setInfoFiles([]);
    setInfoUploadStatus({});
    if (infoFileInputRef.current) infoFileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      // Re-assign hours to remaining files
      return newFiles.map((f, i) => ({
        ...f,
        hour: TIME_SLOTS[i]?.hour,
      }));
    });
  };

  const handleBulkUpload = async () => {
    if (files.length === 0) {
      alert("Please select at least one image");
      return;
    }

    setUploading(true);
    setUploadStatus({});

    const API = process.env.REACT_APP_BACKEND_URL;

    for (const fileData of files) {
      const { file, hour } = fileData;

      try {
        setUploadStatus(prev => ({ ...prev, [hour]: "uploading" }));

        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(date.getFullYear()));
        formData.append("month", String(date.getMonth() + 1));
        formData.append("day", String(date.getDate()));
        formData.append("hour", String(hour));

        const response = await fetch(`${API}/api/qimen/upload-chart-image`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (data.success) {
          setUploadStatus(prev => ({ ...prev, [hour]: "success" }));
        } else {
          setUploadStatus(prev => ({ ...prev, [hour]: "error" }));
        }
      } catch (err) {
        console.error(`Upload error for ${hour}:00:`, err);
        setUploadStatus(prev => ({ ...prev, [hour]: "error" }));
      }
    }

    setUploading(false);
  };

  const successCount = Object.values(uploadStatus).filter(s => s === "success").length;
  const infoSuccessCount = Object.values(infoUploadStatus).filter(s => s === "success").length;

  // Handle info image bulk upload (date+hour specific)
  const handleInfoBulkUpload = async () => {
    if (infoFiles.length === 0) {
      alert("Please select at least one info image");
      return;
    }

    setUploading(true);
    setInfoUploadStatus({});

    for (const fileData of infoFiles) {
      const { file, hour, displayHour } = fileData;

      try {
        setInfoUploadStatus(prev => ({ ...prev, [hour]: "uploading" }));

        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(date.getFullYear()));
        formData.append("month", String(date.getMonth() + 1));
        formData.append("day", String(date.getDate()));
        formData.append("hour", String(displayHour));

        const response = await fetch(`${API}/info/upload`, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (data.success) {
          setInfoUploadStatus(prev => ({ ...prev, [hour]: "success" }));
        } else {
          setInfoUploadStatus(prev => ({ ...prev, [hour]: "error" }));
        }
      } catch (err) {
        console.error(`Info upload error for ${hour}:00:`, err);
        setInfoUploadStatus(prev => ({ ...prev, [hour]: "error" }));
      }
    }

    setUploading(false);
  };

  // Function to proceed to next day
  const handleNextDay = () => {
    files.forEach(f => URL.revokeObjectURL(f.preview));
    infoFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setFiles([]);
    setInfoFiles([]);
    setUploadStatus({});
    setInfoUploadStatus({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (infoFileInputRef.current) infoFileInputRef.current.value = "";
    setDate(addDays(date, 1));
  };

  return (
    <div className="bulk-upload-page" data-testid="bulk-upload-page">
      <div className="upload-bg-pattern" />
      <div className="bulk-upload-container">
        {/* Back Button */}
        <BackButton />

        {/* Header */}
        <div className="upload-header">
          <div className="upload-logo-mark">
            <Upload className="upload-logo-icon" />
          </div>
          <h1 className="upload-title" data-testid="bulk-upload-title">
            Bulk Upload Charts
          </h1>
          <p className="upload-subtitle">
            Upload up to 24 chart images for a full day (01:00 - 24:00)
          </p>
        </div>

        {/* Date Picker */}
        <div className="bulk-date-picker">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="bulk-date-btn"
                data-testid="bulk-date-picker"
              >
                <CalendarIcon size={18} />
                {format(date, "MMMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="calendar-popover" align="center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  if (d) {
                    setDate(d);
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Tab Selector for Charts and Info Images */}
        <div className="bulk-upload-tabs">
          <div className="bulk-tabs-list">
            <button
              className={`bulk-tab ${activeTab === 'charts' ? 'active' : ''}`}
              data-state={activeTab === 'charts' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('charts')}
              data-testid="charts-tab"
            >
              <Image size={16} />
              Chart Images
            </button>
            <button
              className={`bulk-tab ${activeTab === 'info' ? 'active' : ''}`}
              data-state={activeTab === 'info' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('info')}
              data-testid="info-tab"
            >
              <Info size={16} />
              Important Info
            </button>
          </div>

          {activeTab === 'charts' && (
            <div className="mt-2">
            {files.length === 0 ? (
              <div
                className={`bulk-dropzone ${dragActive ? "drag-active" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="bulk-dropzone"
              >
                <Upload size={48} className="dropzone-icon" />
                <p className="dropzone-text">
                  Drag & drop up to 24 chart images here, or click to select
                </p>
                <p className="dropzone-hint">
                  Images will be assigned to timeslots in selection order (01:00, 02:00, ...)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleInputChange}
                  className="hidden-input"
                  data-testid="bulk-file-input"
                />
              </div>
            ) : (
              <div className="bulk-files-container">
                <div className="bulk-files-header">
                  <span className="bulk-files-count">{files.length} chart{files.length !== 1 ? "s" : ""} selected</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFiles}
                    disabled={uploading}
                    data-testid="clear-all-btn"
                  >
                    <X size={16} />
                    Clear All
                  </Button>
                </div>

                <div className="bulk-preview-grid">
                  {files.map((fileData, index) => (
                    <div
                      key={index}
                      className={`bulk-preview-item ${uploadStatus[fileData.hour] || ""}`}
                      data-testid={`preview-item-${fileData.hour}`}
                    >
                      <div className="preview-header">
                        <span className="preview-time">{TIME_SLOTS[index]?.label}</span>
                        {uploadStatus[fileData.hour] === "success" && <Check size={14} className="status-icon success" />}
                        {uploadStatus[fileData.hour] === "uploading" && <Loader2 size={14} className="status-icon uploading animate-spin" />}
                        {uploadStatus[fileData.hour] === "error" && <X size={14} className="status-icon error" />}
                      </div>
                      <div className="preview-image-wrap">
                        <img src={fileData.preview} alt={`Chart ${TIME_SLOTS[index]?.label}`} />
                        {!uploading && !uploadStatus[fileData.hour] && (
                          <button
                            className="preview-remove-btn"
                            onClick={() => removeFile(index)}
                            data-testid={`remove-file-${index}`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <span className="preview-filename">{fileData.file.name}</span>
                    </div>
                  ))}
                </div>

                {files.length < 24 && !uploading && (
                  <Button
                    variant="outline"
                    className="add-more-btn"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="add-more-btn"
                  >
                    <Image size={16} />
                    Add More ({24 - files.length} slots remaining)
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleInputChange}
                      className="hidden-input"
                    />
                  </Button>
                )}
              </div>
            )}

            <div className="bulk-upload-actions">
              <Button
                className="upload-submit-btn"
                onClick={handleBulkUpload}
                disabled={files.length === 0 || uploading}
                data-testid="bulk-upload-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Upload {files.length} Chart{files.length !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>

            {successCount > 0 && (
              <div className="bulk-upload-success">
                <p className="success-message">{successCount} chart{successCount !== 1 ? "s" : ""} uploaded successfully!</p>
              </div>
            )}
            </div>
          )}

          {activeTab === 'info' && (
            <div className="mt-2">
            {infoFiles.length === 0 ? (
              <div
                className={`bulk-dropzone ${infoDragActive ? "drag-active" : ""}`}
                onDragEnter={(e) => { e.preventDefault(); setInfoDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setInfoDragActive(false); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); setInfoDragActive(false); assignInfoFilesToSlots(e.dataTransfer.files); }}
                onClick={() => infoFileInputRef.current?.click()}
                data-testid="info-dropzone"
              >
                <Info size={48} className="dropzone-icon" />
                <p className="dropzone-text">
                  Drag & drop up to 24 info images here, or click to select
                </p>
                <p className="dropzone-hint">
                  Info images for {format(date, "MMM d, yyyy")} — assigned to timeslots in order (01:00, 02:00, ...)
                </p>
                <input
                  ref={infoFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => assignInfoFilesToSlots(e.target.files)}
                  className="hidden-input"
                  data-testid="info-file-input"
                />
              </div>
            ) : (
              <div className="bulk-files-container">
                <div className="bulk-files-header">
                  <span className="bulk-files-count">{infoFiles.length} info image{infoFiles.length !== 1 ? "s" : ""} selected</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearInfoFiles}
                    disabled={uploading}
                    data-testid="clear-info-btn"
                  >
                    <X size={16} />
                    Clear All
                  </Button>
                </div>

                <div className="bulk-preview-grid">
                  {infoFiles.map((fileData, index) => (
                    <div
                      key={index}
                      className={`bulk-preview-item ${infoUploadStatus[fileData.hour] || ""}`}
                      data-testid={`info-preview-${fileData.hour}`}
                    >
                      <div className="preview-header">
                        <span className="preview-time">{TIME_SLOTS[index]?.label}</span>
                        {infoUploadStatus[fileData.hour] === "success" && <Check size={14} className="status-icon success" />}
                        {infoUploadStatus[fileData.hour] === "uploading" && <Loader2 size={14} className="status-icon uploading animate-spin" />}
                        {infoUploadStatus[fileData.hour] === "error" && <X size={14} className="status-icon error" />}
                      </div>
                      <div className="preview-image-wrap">
                        <img src={fileData.preview} alt={`Info ${TIME_SLOTS[index]?.label}`} />
                      </div>
                      <span className="preview-filename">{fileData.file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bulk-upload-actions">
              <Button
                className="upload-submit-btn info-upload-btn"
                onClick={handleInfoBulkUpload}
                disabled={infoFiles.length === 0 || uploading}
                data-testid="info-upload-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Info size={18} />
                    Upload {infoFiles.length} Info Image{infoFiles.length !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>

            {infoSuccessCount > 0 && (
              <div className="bulk-upload-success">
                <p className="success-message">{infoSuccessCount} info image{infoSuccessCount !== 1 ? "s" : ""} uploaded successfully!</p>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Next Day Button */}
        {(successCount > 0 || infoSuccessCount > 0) && (
          <div className="bulk-upload-success">
            <div className="success-actions">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/charts")}
                data-testid="view-uploaded-btn"
              >
                View Uploaded Charts
              </Button>
              <Button
                className="next-day-btn"
                onClick={handleNextDay}
                data-testid="next-day-btn"
              >
                Upload Next Day ({format(addDays(date, 1), "MMM d, yyyy")})
                <ChevronRight size={18} />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="page-footer">
          <p>Qi Men Dun Jia Plotter</p>
          <p className="footer-copyright">Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</p>
        </footer>
      </div>
    </div>
  );
}
